/* eslint-disable react/prop-types */
import { useState } from 'react';
import { useFetchRevenueReport } from './Hook/Index';
import {
  Table,
  Row,
  Col,
  Statistic,
  Card,
  Empty,
  Alert,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import SharedFilters from './SharedFilters';
import PropTypes from 'prop-types';
import { useAuthStore } from '../../store/useAuthStore';
import { formatCurrency } from '../../utils/utils';
import ReportTrendChart from './ReportTrendChart';

const RevenueTab = ({ operatorId, active = false, name }) => {
  const { t } = useTranslation('reportPage');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const { user } = useAuthStore();

  const [rfidFilter, setRfidFilter] = useState('ALL'); // Default to showing all data
  const [locationId, setLocationId] = useState(null);
  const [chargerId, setChargerId] = useState(null);

  const { data: response, isLoading } = useFetchRevenueReport(
    {
      operatorId,
      fromDate: dateRange[0],
      toDate: dateRange[1],
      isRFID:
        rfidFilter === 'ALL' ? undefined : rfidFilter === 'RFID' ? true : false,
      locationId,
      chargerId,
    },
    { enabled: active }
  );

  const data = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response)
      ? response
      : [];
  const aggregatedData =
    operatorId && response?.totalRevenue !== undefined ? response : null;


  const handleExportCsv = () => {
    if (!data.length) return;

    const csvData = data.map((record) => ({
      OperatorName: record.operatorName,
      TotalRevenue: record.totalRevenue.toFixed(2),
      TotalVat: record.totalTax.toFixed(2),
      TotalOperatorCommission: record.totalOperatorCommission.toFixed(2),
      TotalLocationOwnerEarning: record.totalLocationOwnerEarning.toFixed(2),
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'RevenueReport.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      ellipsis: true,
    },
    {
      title: t('totalRevenue'),
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'center',
      render: (value) => `${formatCurrency(value.toFixed(2))}`,
    },
    {
      title: t('totalTax'),
      dataIndex: 'totalTax',
      key: 'totalTax',
      align: 'center',
      render: (value) => `${formatCurrency(value.toFixed(2))}`,
      responsive: ['md'],
    },
    {
      title: t('totalOperatorCommission'),
      dataIndex: 'totalOperatorCommission',
      key: 'totalOperatorCommission',
      align: 'center',
      render: (value) => `${formatCurrency(value.toFixed(2))}`,
      responsive: ['lg'],
      ellipsis: true,
    },
    {
      title: t('totalLocationOwnerEarning'),
      dataIndex: 'totalLocationOwnerEarning',
      key: 'totalLocationOwnerEarning',
      align: 'center',
      render: (value) => `${formatCurrency(value.toFixed(2))}`,
      responsive: ['lg'],
      ellipsis: true,
    },
  ];

  const handleFiltersChange = (filters) => {
    setDateRange(filters.dateRange);
    setRfidFilter(filters.rfidFilter);
    setChargerId(filters.chargerId);
    setLocationId(filters.locationId);
  };

  const renderSummaryCards = (aggregatedData, data, t, isLoading) => {
    if (aggregatedData) {
      return (
        <>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size='small' 
              title={t('totalRevenue')} 
              loading={isLoading}
              style={{ marginBottom: 16 }}
            >
              <Statistic
                value={formatCurrency(aggregatedData.totalRevenue?.toFixed(2) || 0)}
                valueStyle={{ fontSize: '16px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size='small'
              title={t('totalTax')} 
              loading={isLoading}
              style={{ marginBottom: 16 }}
            >
              <Statistic
                value={formatCurrency(aggregatedData.totalTax?.toFixed(2) || 0)}
                valueStyle={{ fontSize: '16px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size='small'
              title={t('totalOperatorCommission')} 
              loading={isLoading}
              style={{ marginBottom: 16 }}
            >
              <Statistic
                value={formatCurrency(aggregatedData.totalOperatorCommission?.toFixed(2) || 0)}
                valueStyle={{ fontSize: '16px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size='small'
              title={t('totalLocationOwnerEarning')} 
              loading={isLoading}
              style={{ marginBottom: 16 }}
            >
              <Statistic
                value={formatCurrency(aggregatedData.totalLocationOwnerEarning?.toFixed(2) || 0)}
                valueStyle={{ fontSize: '16px' }}
              />
            </Card>
          </Col>
        </>
      );
    } else {
      return (
        <Col xs={24} sm={12} md={6}>
          <Card 
            size='small' 
            title={t('totalRevenue')} 
            loading={isLoading}
            style={{ marginBottom: 16 }}
          >
            {data.length > 0 && (
              <Statistic
                value={formatCurrency(data
                  .reduce((sum, record) => sum + record.totalRevenue, 0)
                  .toFixed(2))}
                valueStyle={{ fontSize: '16px' }}
              />
            )}
          </Card>
        </Col>
      );
    }
  };

  return (
    <div style={{ padding: '0 8px' }}>
      {operatorId && (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
        <Alert
          message={t('dashboard:alertMessage', {
            operatorName: name,
          })}
          type="info"
          showIcon
          closable
          style={{
            marginBottom: 24,
            border: 'none',
            backgroundColor: '#d7e1dd',
            fontSize: '14px',
          }}
        />
      )}
      <SharedFilters
        operatorId={operatorId}
        onFiltersChange={handleFiltersChange}
        onExport={handleExportCsv}
        isLoading={isLoading}
      />
      <Row gutter={[16, 16]} className="mb-4">
        {renderSummaryCards(aggregatedData, data, t, isLoading)}
      </Row>
      <ReportTrendChart
        title={t('revenueTrend')}
        series={response?.series}
        loading={isLoading}
        yFormatter={(val) => formatCurrency(Number(val).toFixed(2))}
        fields={[{ key: 'totalRevenue', label: t('totalRevenue') }]}
      />
      {!aggregatedData && data.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={data}
            loading={isLoading}
            rowKey="_id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            size="small"
            style={{ 
              fontSize: '14px',
              minWidth: '600px'
            }}
          />
        </div>
      ) : (
        !aggregatedData && <Empty description={t('noDataAvailable')} />
      )}
    </div>
  );
}

RevenueTab.propTypes = {
  active: PropTypes.bool,
  operatorId: PropTypes.any,
  name: PropTypes.string,
};

export default RevenueTab;
